import React from "react";
import localforage from "localforage";
import { getNegotiationBalance, type NegotiationBalance } from "../../services/negotiationBalance";
import { BALANCE_POLL_MS } from "./negotiationBalanceFormat";

export type UseNegotiationBalanceOptions = {
  /** False never reads: tutorial (mock) negotiation, or no permission to see the balance. */
  enabled: boolean;
  /** Poll interval while the tab is visible and the company has an account; 0 disables it. */
  pollMs?: number;
  /** A change starts over from an empty state (for example a new negotiation mode). */
  resetKey?: string | number;
};

export type NegotiationBalanceState = {
  /** null: no answer yet, or Payments does not offer the balance to this company. */
  snapshot: NegotiationBalance | null;
  loaded: boolean;
  /** The last read failed; `snapshot` is still the previous good answer. */
  stale: boolean;
};

export type UseNegotiationBalance = NegotiationBalanceState & {
  /**
   * Re-reads now (after a bid answer, a refusal or a closed recharge). It
   * sends nothing while the last answer had nothing to show (dormant),
   * so a bid on a page without a balance never adds a Payments request.
   */
  refresh: () => void;
  /** Called whenever a read observes a new balance revision. Returns the unsubscribe. */
  subscribe: (listener: (snapshot: NegotiationBalance) => void) => () => void;
};

const EMPTY: NegotiationBalanceState = { snapshot: null, loaded: false, stale: false };

/**
 * Answers that will not change by asking again: a Payments build without the
 * routes, or a session that cannot read them. Polling and refresh stop until
 * the page loads again.
 */
const DORMANT_FAILURES: ReadonlySet<string> = new Set([
  "route_missing",
  "forbidden",
  "unauthorized",
]);

/**
 * Reads GET /payment/negotiation-balance on mount and keeps it fresh: every
 * `pollMs` while the tab is visible, and again the moment the tab is looked at.
 *
 * The hook goes dormant while the last answer has nothing to show: Payments
 * answers negotiation_balance_disabled (its flag is off), the company was
 * never activated, or the routes are missing or forbidden. Then neither the
 * poll nor `refresh` reads again, so a dark ship costs one GET per page load
 * and nothing more, bids included. Late answers of an older read are dropped,
 * and every timer and listener is removed on unmount.
 */
export function useNegotiationBalance({
  enabled,
  pollMs = BALANCE_POLL_MS,
  resetKey,
}: UseNegotiationBalanceOptions): UseNegotiationBalance {
  const [state, setState] = React.useState<NegotiationBalanceState>(EMPTY);
  const snapshotRef = React.useRef<NegotiationBalance | null>(null);
  const sequenceRef = React.useRef(0);
  const dormantRef = React.useRef(false);
  const listenersRef = React.useRef(new Set<(snapshot: NegotiationBalance) => void>());
  const readRef = React.useRef<(initial: boolean) => Promise<void>>(async () => {});

  React.useEffect(() => {
    let disposed = false;
    sequenceRef.current += 1;
    snapshotRef.current = null;
    dormantRef.current = false;
    setState(EMPTY);
    if (!enabled) {
      readRef.current = async () => {};
      return () => {
        disposed = true;
      };
    }

    // `initial` is the one read of a mount; every later read (poll, focus,
    // refresh) is skipped while dormant.
    const read = async (initial: boolean) => {
      if (disposed || (dormantRef.current && !initial)) return;
      const sequence = ++sequenceRef.current;
      let jwt: string | null | undefined;
      try {
        jwt = await localforage.getItem<string>("JWT");
      } catch {
        jwt = undefined;
      }
      if (disposed || sequence !== sequenceRef.current) return;
      if (!jwt) {
        setState((previous) => ({ ...previous, loaded: true, stale: previous.snapshot !== null }));
        return;
      }
      const result = await getNegotiationBalance(jwt);
      if (disposed || sequence !== sequenceRef.current) return;
      if (result.ok) {
        const previous = snapshotRef.current;
        const next = result.value;
        snapshotRef.current = next;
        dormantRef.current = next === null;
        setState({ snapshot: next, loaded: true, stale: false });
        if (next && previous && previous.revision !== next.revision) {
          listenersRef.current.forEach((listener) => listener(next));
        }
        return;
      }
      if (DORMANT_FAILURES.has(result.failure.code)) {
        dormantRef.current = true;
        snapshotRef.current = null;
        setState({ snapshot: null, loaded: true, stale: false });
        return;
      }
      setState((previous) => ({ ...previous, loaded: true, stale: true }));
    };
    readRef.current = read;
    void read(true);

    const polling = () =>
      document.visibilityState === "visible" &&
      !dormantRef.current &&
      Boolean(snapshotRef.current?.account);
    const timer =
      pollMs > 0
        ? setInterval(() => {
            if (polling()) void read(false);
          }, pollMs)
        : undefined;
    const onVisibilityChange = () => {
      if (polling()) void read(false);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      disposed = true;
      sequenceRef.current += 1;
      readRef.current = async () => {};
      if (timer !== undefined) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled, pollMs, resetKey]);

  const refresh = React.useCallback(() => {
    void readRef.current(false);
  }, []);
  const subscribe = React.useCallback((listener: (snapshot: NegotiationBalance) => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  return { ...state, refresh, subscribe };
}

/** Whether the company has already opened its negotiation balance. */
export function hasNegotiationAccount(
  snapshot: NegotiationBalance | null,
): snapshot is NegotiationBalance & { account: NonNullable<NegotiationBalance["account"]> } {
  return snapshot !== null && snapshot.account !== null;
}
